import { supabase } from './supabase';
import { MedicationInfo } from '../data/medications';

export interface MedicationInfoFull {
  id: string;
  name: string;
  activeSubstance?: string;
  activeSubstanceAmount?: string;
  unit: string;
  brandName?: string;
  notes?: string;
  userId?: string;
  createdAt?: string;
  updatedAt?: string;
}

// Konwersja między formatem aplikacji a formatem bazy danych
const toDbFormat = (data: MedicationInfoFull) => ({
  id: data.id,
  name: data.name,
  active_substance: data.activeSubstance || null,
  active_substance_amount: data.activeSubstanceAmount || null,
  unit: data.unit,
  brand_name: data.brandName || null,
  notes: data.notes || null
});

const fromDbFormat = (dbData: any): MedicationInfoFull => ({
  id: dbData.id,
  name: dbData.name,
  activeSubstance: dbData.active_substance,
  activeSubstanceAmount: dbData.active_substance_amount,
  unit: dbData.unit,
  brandName: dbData.brand_name,
  notes: dbData.notes,
  userId: dbData.user_id,
  createdAt: dbData.created_at,
  updatedAt: dbData.updated_at
});

export const medicationInfoService = {
  // Pobierz wszystkie leki (wszyscy użytkownicy mogą widzieć wszystkie leki)
  getAll: async (): Promise<MedicationInfo[]> => {
    const { data, error } = await supabase
      .from('medication_info')
      .select('*')
      .order('name', { ascending: true });

    if (error) {
      console.error('Błąd pobierania leków:', error);
      throw error;
    }

    return (data || []).map(dbData => ({
      name: dbData.name,
      unit: dbData.unit
    }));
  },

  // Pobierz leki użytkownika
  getByUser: async (): Promise<MedicationInfoFull[]> => {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('Użytkownik nie jest zalogowany');
    }

    const { data, error } = await supabase
      .from('medication_info')
      .select('*')
      .eq('user_id', user.id)
      .order('name', { ascending: true });

    if (error) {
      console.error('Błąd pobierania leków użytkownika:', error);
      throw error;
    }

    return (data || []).map(fromDbFormat);
  },

  // Utwórz nowy lek
  create: async (medication: MedicationInfoFull): Promise<MedicationInfoFull> => {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError || !user) {
      throw new Error('Użytkownik nie jest zalogowany');
    }

    const dbData = {
      ...toDbFormat(medication),
      user_id: user.id
    };

    const { data: insertedData, error } = await supabase
      .from('medication_info')
      .insert([dbData])
      .select()
      .single();

    if (error) {
      console.error('Błąd tworzenia leku:', error);
      throw error;
    }

    return fromDbFormat(insertedData);
  },

  // Zaktualizuj lek
  update: async (id: string, medication: MedicationInfoFull): Promise<MedicationInfoFull> => {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('Użytkownik nie jest zalogowany');
    }

    const dbData = toDbFormat(medication);
    const { data: updatedData, error } = await supabase
      .from('medication_info')
      .update(dbData)
      .eq('id', id)
      .eq('user_id', user.id)
      .select()
      .single();

    if (error) {
      console.error('Błąd aktualizacji leku:', error);
      throw error;
    }

    return fromDbFormat(updatedData);
  },

  // Usuń lek
  delete: async (id: string): Promise<void> => {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('Użytkownik nie jest zalogowany');
    }

    const { error } = await supabase
      .from('medication_info')
      .delete()
      .eq('id', id)
      .eq('user_id', user.id);

    if (error) {
      console.error('Błąd usuwania leku:', error);
      throw error;
    }
  }
};
